import { ModuleType, type AirHandlerUnit, type BoilerRoom, type Floor, type Module } from "@eisox/api/core-api";
import { Button, Heading, Typography } from "@eisox/design-system";
import { PlusIcon } from "@eisox/icons";
import { Link, Outlet } from "@tanstack/react-router";

import { m } from "~/paraglide/messages";
import type { Permission } from "~/types/auth.type";
import type { EnhancedGateway } from "~/types/gateway.type";

import { BOILER_ROOM_MODULE_TYPES, getModuleLabel } from "../helpers";
import { AddModuleDialog } from "./add-module-dialog";

interface ModulesLayoutProps {
  buildingId: string;
  modules: Module[];
  boilerRooms: BoilerRoom[];
  airHandlerUnits: AirHandlerUnit[];
  gateways: EnhancedGateway[];
  floors: Floor[];
  permissions: Permission;
}

export function ModulesLayout({
  buildingId,
  modules,
  boilerRooms,
  airHandlerUnits,
  gateways,
  floors,
  permissions,
}: ModulesLayoutProps) {
  const listedModules = modules.filter(
    md => BOILER_ROOM_MODULE_TYPES.includes(md.type) || md.type === ModuleType.ECLYPSE_RELAIS,
  );

  return (
    <div className="gap-10 flex h-full w-full">
      <div className="max-w-87.5 min-w-25 flex flex-1 flex-col">
        <Heading as="h2" size={4}>
          {m.module_settings_subtitle()}
        </Heading>
        <div className="mt-2.5 gap-1.25 flex flex-col overflow-y-auto">
          {listedModules.map(md => {
            const label = getModuleLabel(md, boilerRooms, airHandlerUnits);
            return (
              <Link
                key={md.id}
                to="/buildings/$buildingId/settings/bms/modules/$moduleId"
                params={{ buildingId, moduleId: md.id }}
                className="p-2.5 rounded-[10px] text-base no-underline hover:bg-primary-gray"
                activeProps={{ className: "bg-primary-gray text-primary-blue" }}
                inactiveProps={{ className: "text-primary-dark-blue" }}
              >
                <Typography>{label}</Typography>
              </Link>
            );
          })}
          {permissions.module?.create && gateways.length > 0 && (
            <AddModuleDialog buildingId={buildingId} gateways={gateways} floors={floors} modules={modules}>
              <Button className="mt-2.5">
                {m.module_settings_add()}
                <PlusIcon />
              </Button>
            </AddModuleDialog>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
